import * as ng from 'angular';
import * as _ from 'underscore';
import * as moment from 'moment';
import * as Highcharts from 'highcharts';

import {Dataset,DatasetField} from '..';

ng.module('e360')
   .factory('lineChartFactory', lineChartFactory);

lineChartFactory.$inject = ['instanceServiceFactory'];
function lineChartFactory(instanceServiceFactory) {
   return instanceServiceFactory(LineChart);
}

export class LineChart {
   static formatValue(field:DatasetField, val) {
      // for the purpose of the FCA velocity chart hour (0-23) is using
      // type 'time', if this is a problem for future charts add a new
      // field type 'hour'
      if (field.type == 'time') {
         if (val == 0) {
            return '12:00 am';
         } else if (val == 12) {
            return '12:00 pm';
         } else {
            return val > 12 ? `${val % 12}:00pm` : `${val % 12}:00am`;
         }
      } else if (field.type == 'date') {
         return moment(val).format('MMM D');
      }

      return val;
   }

   static nativeValue(field:DatasetField, val) {
      return field.type.indexOf('date') == 0 ? moment(val).toDate() : val;
   }

   static defaults = {
      'email-total': {
         tooltip: {
            pointFormat: [
               '<span style="color:{series.color}">\u25CF</span> Sent: <b>{point.y}</b>',
               '<span style="color:{series.color}">\u25CF</span> Opened: <b>{point.opened}</b>'
            ].join('<br/>')
         }
      },
      'fca-velocity-hourly': {
         plotOptions: {
            line: {
               dataLabels: { enabled: true },
               // disable data point tooltips
               enableMouseTracking: false,
               events: {
                  // disable click event on legend that toggles line chart
                  legendItemClick: () => false
               }
            }
         }
      }
   };

   protected chart:Highcharts.ChartObject;
   static $inject = ['ds'];
   constructor(protected ds:Dataset) {
      let defaults = LineChart.defaults[ds.id] || { };

      let options = _.extend({
         chart: {
            renderTo: ds.id
         },
         xAxis: {
            labels: {
               // use point.name for xAxis by default
               type: 'category',
               rotation: -45
            }
         },
         yAxis: {
            title: {text: null},
            floor: 0
         },
         title: {
            text: ds.title
         }
      }, defaults);

      if (ds.xAxis.type.indexOf('date') == 0) {
         options.xAxis.type = 'datetime';
         // minimum interval of one day
         options.xAxis.minTickInterval = 24 * 3600 * 1000;
      }

      this.chart = new Highcharts.Chart(options);
   }

   setData(data:any[]) {
      while (this.chart.series.length > 0) {
         this.chart.series.pop().remove();
      }

      if (data.length === 0) return;

      let xAxis = this.ds.xAxis;
      let yAxis = this.ds.yAxis;

      if (this.ds.groupBy) {
         let grouped = _.groupBy(data, this.ds.groupBy.name);

         _.each(grouped, (val, key) => {
            this.chart.addSeries({
               name: key,
               data: _.map(val, (d) => {
                  return _.extend(d, {
                     name: LineChart.formatValue(xAxis, d[xAxis.name]),
                     x: LineChart.nativeValue(xAxis, d[xAxis.name]),
                     y: d[yAxis.name]
                  });
               })
            }, false); // false to disable default redraw
         });

         this.chart.redraw();
      } else {
         this.chart.update({
            xAxis: {
               categories: _.map(data, d => LineChart.formatValue(xAxis, d[xAxis.name])),
               title: { text: xAxis.label }
            },
            yAxis: {
               title: { text: yAxis.label }
            }
         });

         this.chart.addSeries({
            name: yAxis.label,
            data: _.map(data, (d) => d[yAxis.name])
         });
      }
   }

   onLoadStart() {
      this.chart.showLoading();
   }

   onLoadFinish() {
      this.chart.hideLoading();
   }
}

export interface LineChartFactory {
   ({ds:Dataset}):LineChart;
}
